import React, { useEffect, useId, useMemo, useRef, useState } from "react";

/**
 * Generic searchable combobox (plan §A6). Controlled: parent owns `value`.
 * Options are normalized: { value, primary, secondary?, search }
 *   - value: returned via onChange when chosen
 *   - primary: main label line
 *   - secondary: optional muted sub-line
 *   - search: lowercase haystack to filter against
 * Filtering PRESERVES the incoming option order (callers' smart-sort is respected).
 */
export default function Combobox({
  value, options = [], onChange, onCreate,
  allowCreate = false, placeholder = "— pilih —", createLabel = "", id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listboxId = useId();

  const selected = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;               // empty query → full list in original order
    return options.filter((o) => o.search.includes(q));
  }, [options, query]);

  const trimmed = query.trim();
  const exact = !!trimmed && options.some((o) => o.value.toLowerCase() === trimmed.toLowerCase());
  const showCreate = allowCreate && trimmed.length > 0 && !exact;
  const totalRows = filtered.length + (showCreate ? 1 : 0);

  const close = () => { setOpen(false); setQuery(""); setActive(0); };
  const choose = (opt) => { onChange(opt.value); close(); };
  const create = () => { if (!trimmed) return; onCreate?.(trimmed); onChange(trimmed); close(); };

  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) close(); };
    // Capture phase: fire before bubble-phase handlers so a click inside a modal that does
    // onMouseDown={e=>e.stopPropagation()} (e.g. SetNameModal) still dismisses the dropdown.
    // Without capture, that stopPropagation (run at React's root) prevents this from ever firing.
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, totalRows - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (showCreate && active === filtered.length) create();
      else if (filtered[active]) choose(filtered[active]);
    } else if (e.key === "Escape") {
      // task 5e: only swallow Escape while actually open — otherwise the menu-close Esc also
      // bubbles to modalStack's document-level listener and closes the HOST modal in the same
      // keypress. Idle (menu already closed), let it propagate so Esc still closes the host
      // modal as normal.
      if (open) { close(); e.stopPropagation(); }
    }
  };

  const inputValue = open ? query : (selected ? selected.primary : "");

  return (
    <div className="combobox" ref={wrapRef}>
      <input
        id={id}
        className="form-input combobox__input"
        role="combobox" aria-expanded={open} aria-controls={listboxId}
        placeholder={placeholder} value={inputValue}
        onFocus={() => setOpen(true)}
        // Same fix as ContactPicker.js's identical bug: `onFocus` alone can never reopen the
        // menu once this input already holds DOM focus without blurring (e.g. right after
        // picking a value, or clearing via "×" — both deliberately avoid stealing focus).
        // `onMouseDown` fires on every click regardless of prior focus state.
        onMouseDown={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
      />
      {value && !open && (
        <button type="button" className="combobox__clear" title="Hapus pilihan"
          onMouseDown={(e) => { e.preventDefault(); onChange(null); }}>×</button>
      )}
      {open && (
        <div className="combobox__menu" role="listbox" id={listboxId}>
          {filtered.length === 0 && !showCreate && <div className="combobox__empty">Tidak ada hasil.</div>}
          {filtered.map((o, i) => (
            <div key={o.value} role="option" aria-selected={o.value === value}
              className={"combobox__option" + (i === active ? " combobox__option--active" : "")}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}>
              <div className="combobox__option-primary">{o.primary}</div>
              {o.secondary && <div className="combobox__option-secondary">{o.secondary}</div>}
            </div>
          ))}
          {showCreate && (
            <div role="option" aria-selected={false}
              className={"combobox__option" + (active === filtered.length ? " combobox__option--active" : "")}
              onMouseEnter={() => setActive(filtered.length)}
              onMouseDown={(e) => { e.preventDefault(); create(); }}>
              <div className="combobox__create">+ Tambah “{trimmed}”{createLabel ? ` (${createLabel} baru)` : ""}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
